import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FileViewer } from '@capacitor/file-viewer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { NativeFileDownloadDialogComponent } from './native-file-download-dialog.component';

@Injectable({
    providedIn: 'root',
})
export class NativeFileViewerService {
    private readonly cacheDirectory = 'file-viewer';

    constructor(private dialog: MatDialog) {}

    async openNativePdf(url: string, filename: string): Promise<void> {
        const safeFilename = this.getNativePdfFilename(filename);
        const path = `${this.cacheDirectory}/${safeFilename}`;
        const dialogRef = this.dialog.open(NativeFileDownloadDialogComponent, {
            disableClose: true,
            data: {
                filename: safeFilename,
            },
        });
        let canceled = false;
        const subscription = dialogRef.afterClosed().subscribe(result => (canceled = result === 'cancel'));

        try {
            await this.ensureCacheDirectory();
            if (canceled) {
                return;
            }

            await this.deleteFile(path);
            if (canceled) {
                return;
            }

            await Filesystem.downloadFile({
                url,
                path,
                directory: Directory.Cache,
            });

            if (canceled) {
                await this.deleteFile(path);
                return;
            }

            const file = await Filesystem.getUri({
                path,
                directory: Directory.Cache,
            });

            await FileViewer.openDocumentFromLocalPath({ path: this.getLocalPathFromUri(file.uri) });
        } finally {
            subscription.unsubscribe();
            if (!canceled) {
                dialogRef.close();
            }
        }
    }

    private async ensureCacheDirectory(): Promise<void> {
        const exists = await Filesystem.stat({
            path: this.cacheDirectory,
            directory: Directory.Cache,
        })
            .then(() => true)
            .catch(() => false);

        if (exists) {
            return;
        }

        try {
            await Filesystem.mkdir({
                path: this.cacheDirectory,
                directory: Directory.Cache,
                recursive: true,
            });
        } catch (error) {
            if (!this.isDirectoryAlreadyExistsError(error)) {
                throw error;
            }
        }
    }

    private async deleteFile(path: string): Promise<void> {
        await Filesystem.deleteFile({
            path,
            directory: Directory.Cache,
        }).catch(() => {});
    }

    private getNativePdfFilename(filename: string): string {
        const safeFilename = (filename || 'document.pdf')
            .split('/')
            .pop()
            .replace(/[^a-zA-Z0-9._-]/g, '_');

        if (safeFilename.toLowerCase().endsWith('.pdf')) {
            return safeFilename;
        }

        return `${safeFilename}.pdf`;
    }

    private getLocalPathFromUri(uri: string): string {
        if (uri.startsWith('file://')) {
            return decodeURIComponent(new URL(uri).pathname);
        }

        return uri;
    }

    private isDirectoryAlreadyExistsError(error: any): boolean {
        return (
            error?.code === 'OS-PLUG-FILE-0010' ||
            error?.message?.includes('Directory already exists') ||
            error?.message?.includes('already exists') ||
            error?.errorMessage?.includes('Directory already exists') ||
            error?.errorMessage?.includes('already exists')
        );
    }
}
